import React from 'react';
import {
  View,
  Text,
  Modal,
  Pressable,
  StyleSheet,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { CloseButton } from './CloseButton';

interface BottomSheetModalProps {
  visible: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  /** Whether to show close button even without title (for tooltips) */
  showCloseButton?: boolean;
}

/**
 * Bottom sheet modal that slides up from the bottom with overlay
 */
export const BottomSheetModal: React.FC<BottomSheetModalProps> = ({
  visible,
  onClose,
  children,
  title,
  showCloseButton = false,
}) => {
  const insets = useSafeAreaInsets();

  // Show header if title exists OR if showCloseButton is true
  const shouldShowHeader = !!title || showCloseButton;

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={onClose}
    >
      {/* Overlay */}
      <Pressable style={styles.overlay} onPress={onClose}>
        <View />
      </Pressable>

      {/* Bottom Sheet Content */}
      <View
        style={[
          styles.bottomSheet,
          { paddingBottom: Math.max(insets.bottom, 24) },
        ]}
      >
        {/* Header - shows with title, or just close button for tooltips */}
        {shouldShowHeader && (
          <View style={[styles.header, !title && styles.headerCloseOnly]}>
            {title ? (
              <Text style={styles.headerTitle}>{title}</Text>
            ) : (
              <View style={styles.headerSpacer} />
            )}
            <CloseButton onPress={onClose} />
          </View>
        )}

        {/* Content */}
        {children}
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
  },
  bottomSheet: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    padding: 24,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  headerCloseOnly: {
    justifyContent: 'flex-end',
  },
  headerSpacer: {
    flex: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: MARKETPLACE_COLORS.textPrimary,
    lineHeight: 27,
  },
});

